import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, type FellowshipDetail } from "../lib/api";
import { fmtMiles, type Units } from "../lib/format";
import { walkerFromId } from "../../shared/avatars.ts";
import { Portrait } from "./Sprite";

export default function Fellowships({ units, userId, onChanged }: { units: Units; userId: string; onChanged?: () => void }) {
  const [details, setDetails] = useState<FellowshipDetail[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const { fellowships } = await api.fellowships();
      setDetails(await Promise.all(fellowships.map((f) => api.fellowship(f.id))));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoaded(true);
    }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try { await fn(); await refresh(); onChanged?.(); } catch (e) { setError((e as Error).message); }
  };
  const create = (e: FormEvent) => { e.preventDefault(); void run(async () => { await api.createFellowship(name); setName(""); }); };
  const join = (e: FormEvent) => { e.preventDefault(); void run(async () => { await api.joinFellowship(code); setCode(""); }); };
  const leave = (id: string) => { if (confirm("Leave this fellowship?")) void run(() => api.leaveFellowship(id)); };

  return (
    <>
      {loaded && details.length === 0 && (
        <div className="card" style={{ marginTop: 10 }}>
          <div className="sub">Walk with friends. Create a fellowship or join one with an invite code.</div>
        </div>
      )}
      {details.map((d) => (
        <div className="card" style={{ marginTop: 10 }} key={d.fellowship.id}>
          <div className="card-head">
            <div>
              <div className="leg-name">{d.fellowship.name}</div>
              <div className="fellow-sub">Invite code <span className="code">{d.fellowship.invite_code}</span> · share it to add walkers</div>
            </div>
            <div className="leg-dist">{d.members.length} {d.members.length === 1 ? "walker" : "walkers"}</div>
          </div>
          <div className="sub" style={{ marginTop: 8 }}>
            Together <b>{fmtMiles(d.combined.total_miles, units)}</b>{d.combined.total_miles > 0 && <>, which puts the party in <em>{d.combined.progress.segment.name}</em></>}.
          </div>
          <ol className="standings">
            {d.members.map((m) => (
              <li key={m.user_id} className={m.user_id === userId ? "me" : undefined}>
                <span className="st-rank">{m.rank}</span>
                <Portrait walker={walkerFromId(m.avatar_id)} size={32} />
                <div><div className="st-name">{m.display_name}{m.role === "leader" ? " · leader" : ""}</div><div className="st-leg">{m.segment_name}</div></div>
                <span className="st-dist">{fmtMiles(m.total_miles, units)}</span>
              </li>
            ))}
          </ol>
          <div style={{ marginTop: 10 }}><button className="btn-danger" onClick={() => leave(d.fellowship.id)}>Leave fellowship</button></div>
        </div>
      ))}
      <div className="card" style={{ marginTop: 10 }}>
        <div className="overline">Start or join one</div>
        <form className="inline-form" onSubmit={create}>
          <input className="input" placeholder="New fellowship name" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} required />
          <button className="btn-secondary" type="submit">Create</button>
        </form>
        <form className="inline-form" onSubmit={join}>
          <input className="input" placeholder="Invite code" value={code} maxLength={8} onChange={(e) => setCode(e.target.value.toUpperCase())} required />
          <button className="btn-secondary" type="submit">Join</button>
        </form>
        {error && <p className="error">{error}</p>}
      </div>
    </>
  );
}
